import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpStatusCode,
} from '@angular/common/http';
import { BehaviorSubject, Observable, from, of, throwError, timer } from 'rxjs';
import { switchMap, catchError, finalize, filter } from 'rxjs/operators';
import { AuthService } from '@reenstudio/app/core/services/auth.service';
import { CustomNGXLoggerService, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { environment } from '@reenstudio/environments/environment';
import { Router } from '@angular/router';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import { UserRole } from '@reenstudio/app/core/enum/user-role';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private requestQueue: HttpRequest<any>[] = [];
  private logger: NGXLogger;

  constructor(
    private authService: AuthService,
    private storageService: StorageService,
    private customLogger: CustomNGXLoggerService,
    private router: Router,
    private snackBarService: SnackbarService
  ) {
    this.logger = customLogger.create({ level: NgxLoggerLevel.DEBUG });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Bypasso interceptor per gli URL che non richiedono autorizzazione
    let requestUrl = request.url;
    let urlValue: string;

    if (requestUrl.indexOf('@open-api') !== -1) {
      urlValue = requestUrl.replace('@open-api/', '');
      request = request.clone({
        url: urlValue,
      });
      return next.handle(request);
    }

    // Esclusione path
    if (this.shouldExclude(request.url)) {
      this.logger.debug(
        request.url,
        'Interceptor. Excluding path from interceptor'
      );
      return next.handle(request);
    }

    const token = this.storageService.getToken();
    //eyJraWQiOiIvTXgzQmUvV2h1S1hPa2VrNk81TjVWZE4vMkZWYmxYZ09vV0hCNHNMbW5jPSIsImFsZyI6IkhTNTEyIn0.eyJzdWIiOiJhNjVlNjIzMC1iMGIxLTcwNzgtYjJjMC0zMzM0NzNjMWUxMDEiLCJjb2duaXRvOmdyb3VwcyI6WyJTaXRvIl0sImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJpc3MiOiJodHRwczovL2NvZ25pdG8taWRwLmV1LXNvdXRoLTEuYW1hem9uYXdzLmNvbS9ldS1zb3V0aC0xX21TaTVaUHI2MyIsImNvZ25pdG86dXNlcm5hbWUiOiJhNjVlNjIzMC1iMGIxLTcwNzgtYjJjMC0zMzM0NzNjMWUxMDEiLCJvcmlnaW5fanRpIjoiYjY1ZGUxYzgtOWFkOS00MzI1LTk0ZDItYzYzMWYyYWE4MTFlIiwiYXVkIjoiNjllMjZwMTJlODhwZTdnZ2xhN3RwYTVyZm4iLCJldmVudF9pZCI6IjgxY2I2ZTk3LWMzNTktNDMwNS05OTlkLThlZWY4YzEwOTk4OCIsInRva2VuX3VzZSI6ImlkIiwiYXV0aF90aW1lIjoxNzE4NjEzOTY2LCJuYW1lIjoiTWFyaW8gRnJhZ2dldHRhIiwiZXhwIjoxNzE4NTE3NTY2LCJpYXQiOjE3MTg1MTM5NjYsImp0aSI6ImE1ODI0NWVhLTk2M2EtNDM4Ni04MGI5LWRjNmRhNzMyMWJlNSIsImVtYWlsIjoiZnJhZ2dldHRhd2ViQGdtYWlsLmNvbSJ9.4WrUBHo8t1m1lTjp4DgqcGrfNapIbuPwO3zJ2O0mrFEJ3UfjYOoOKNvS3B9IalQV3VfxS5sEQ49idUz9-muZKA
    if (!token || this.authService.isTokenExpired(token)) {
      if (token) {
        if (!this.isRefreshing) {
          this.isRefreshing = true;
          this.refreshTokenSubject.next(null);

          return from(this.refreshToken()).pipe(
            switchMap((res) => {
              if (res && res.idToken) {
                this.storageService.setToken(res.idToken);
                this.refreshTokenSubject.next(res.idToken);
                return next
                  .handle(this.addTokenHeader(request, res.idToken))
                  .pipe(
                    catchError((error) =>
                      this.handleError(error, request, next)
                    )
                  );
              } else {
                return throwError('Interceptor. Failed to refresh token');
              }
            }),
            finalize(() => {
              this.isRefreshing = false;
            })
          );
        } else {
          return this.addToQueue(request, next);
        }
      } else {
        return next.handle(request);
      }
    } else {
      return next
        .handle(this.addTokenHeader(request, token))
        .pipe(catchError((error) => this.handleError(error, request, next)));
    }
  }

  // Metodo per gestire la Promise di refreshToken()
  private async refreshToken() {
    try {
      const tokens = await this.authService.refreshToken();
      this.logger.debug('Interceptor. Token refreshed', tokens);
      return tokens;
    } catch (error) {
      this.logger.debug('Interceptor. Refresh token error', error);
      // this.authService.signOut();
      return false;
    }
  }

  private addToQueue(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return new Observable((observer) => {
      this.requestQueue.push(request);
      const subscription = this.refreshTokenSubject
        .pipe(
          filter((token) => !!token),
          switchMap(() => {
            const token = this.storageService.getToken();
            if (token) {
              const queuedRequest = this.requestQueue.shift();
              if (queuedRequest) {
                return of(queuedRequest);
              }
            }
            return throwError(
              'Interceptor. No queued request or token available'
            );
          }),
          catchError((error) => {
            // Se la coda è vuota o il token è ancora in fase di aggiornamento,
            // ritenta dopo un breve periodo di tempo
            if (
              error === 'Interceptor. No queued request or token available' ||
              this.isRefreshing
            ) {
              return timer(100).pipe(
                switchMap(() => this.addToQueue(request, next))
              );
            } else {
              return throwError(error);
            }
          }),
          switchMap((queuedRequest) => {
            if (queuedRequest instanceof HttpRequest) {
              return next
                .handle(
                  this.addTokenHeader(
                    queuedRequest,
                    this.storageService.getToken()!
                  )
                )
                .pipe(
                  catchError((error) =>
                    this.handleError(error, queuedRequest, next)
                  )
                );
            } else {
              // Se queuedRequest non è un'istanza di HttpRequest, potrebbe essere un'istanza di HttpEvent.
              // Possiamo trattarlo come un caso normale e passare direttamente l'evento all'observer.
              return of(queuedRequest);
            }
          })
        )
        .subscribe({
          next: (response) => {
            observer.next(response);
          },
          error: (error) => {
            observer.error(error);
          },
          complete: () => {
            observer.complete();
            // if (this.storageCheck()) {
            //   console.log('MEMORIA BLOCK');
            //   this.authService.signOut();
            //   this.snackBarService.error('Errore memoria inatteso.');
            // }
            subscription.unsubscribe();
          },
        });
    });
  }

  private addTokenHeader(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldExclude(url: string): boolean {
    return url.startsWith(environment.api + 'auth');
  }

  // Metodo per gestire l'errore durante l'intercettazione delle richieste
  private handleError(
    error: any,
    request: HttpRequest<any>,
    next: HttpHandler
  ) {
    if (error.status === HttpStatusCode.Forbidden) {
      this.logger.error('Interceptor. Request status code: ', error.status);
      this.logger.error('Interceptor. Token refreshing...');
      return this.addToQueue(request, next);
    } else {
      this.logger.error(error);
      // this.authService.signOut();
      // Restituisci un'Observable<never> coerente
      return throwError(error);
    }
  }

  storageCheck() {
    if (this.storageService.getLevel() === UserRole.SITE) {
      return (
        !this.storageService.getCers()?.length ||
        !this.storageService.getCer() ||
        !this.storageService.getSites()?.length ||
        !this.storageService.getSite()
      );
    } else {
      return !this.storageService.getCer() || !this.storageService.getSite();
    }
  }
}
